import { DecimalPipe } from '@angular/common';
import { Component, Injector, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 *  KpiWidget Widget
 */
@Component({
  selector: 'kpi-widget',
  templateUrl: './kpi-widget.component.html',
  styleUrls: ['./kpi-widget.component.scss']
})
export class KpiWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Selected KPIs
   */
  selectedKPIs: any[];

  /**
   * Text scale
   */
  textScale = 1;

  /**
   * Disabled indexes
   */
  disabledIndexes: number[] = [];

  /**
   * Color Pallete
   */
  colloPallete: string[] = ['rgb(177,178,180)',
    'rgb(98,100,104)',
    'rgb(0,0,0)',
    'rgb(219,214,199)',
    'rgb(277,6,19)',
    'rgb(212,213,214)', 'rgb(152,112,166)', 'rgb(98, 100, 104)', 'rgb(153,154,157)', 'rgb(230,225,216)',
    'rgb(171,192,231)', 'rgb(255, 238 , 171)', 'rgb(255, 184, 127)'];

  /**
  * Constructor
  */
  constructor(injector: Injector,
    private decimalPipe: DecimalPipe,
    private translate: TranslateService) {
    super(injector, 'kpi', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
  }

  /**
   * Returns KPIs
   */
  getKpis() {
    this.selectedKPIs = this.widgetConfig.isProperty ? ['marketValue', 'netInitialYield', 'capitalizationRate', 'marketValuePerSqFt', 'reversionaryYield', 'vacency', 'contractualRent', 'walt',
      'capitalExpenditure', 'netRent', 'overUnderrent', 'totalRentalValue'] : ['propertiesTotal', 'propertiesFreehold', 'marketValue', 'niy', 'nry', 'marketValueSqFt', 'ney', 'propertiesLeasehold', 'propertiesMixed', 'lettableArea', 'totalGrossRent',
      'overUnderrent', 'vacancyRate', 'totaNetRent', 'walt', 'totalRentalValue', 'totalContractedRent']
  }

  /**
   * Returns Background Color
  * @param index {number} KPI index
   */
  getBackroundColor(index: number) {
    const colorIndex = index % 6 === 0 ? index / 6 : Math.floor(index / 6);
    return this.colloPallete[colorIndex];
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useReadOnlySaveChangesHandler();
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.getKpis();
    for (let i = 0; i < this.selectedKPIs.length; i++) {
      this.disabledIndexes.push(i);
    }

    this.mockupData$ = this.widgetService.loadEmpty();

    this.widgetData$ = this.widgetService.load(this.widgetConfig.isProperty ? 'propertyKpiValue' : 'portfolioKpiValue', this.getReport());
    this.widgetData$.pipe(first()).subscribe(data => this.widgetData = data);
  }

  /**
   * Returns KPI name
  * @param key {string} KPI key
   */
  getKpiName(key: string) {
    let label: string;
    switch (key) {
      case "propertiesTotal":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.propertiesTotal');
        break;
      case "propertiesFreehold":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.propertiesFreehold');
        break;
      case "propertiesLeasehold":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.propertiesLeasehold');
        break;
      case "propertiesMixed":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.propertiesMixed');
        break;
      case "lettableArea":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.lettableArea');
        break;
      case "totalGrossRent":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.totalGrossRent');
        break;
      case "overUnderrent":
        label = this.widgetData[key] <= 0 ? this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.overrent') : this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.underrent');
        break;
      case "vacancyRate":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.vacancyRate');
        break;
      case "totaNetRent":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.totaNetRent');
        break;
      case "totalRentalValue":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.totalRentalValue');
        break;
      case "walt":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpiRent.walt');
        break;
      case "marketValue":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpi.marketValue');
        break;
      case "marketValueSqFt":
        label = `${this.translate.instant('widgets.portfolioValuationPortfolioKpi.marketValue')} ${this.unitOfLength}²`;
        break;
      case "niy":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpi.niy');
        break;
      case "nry":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpi.nry');
        break;
      case "ney":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpi.ney');
        break;
      case "totalContractedRent":
        label = this.translate.instant('widgets.portfolioValuationPortfolioKpi.totalContractedRent');
        break;
      case "netInitialYield":
        label = this.translate.instant('widgets.propertyKpi.netInitialYield');
        break;
      case "capitalizationRate":
        label = this.translate.instant('widgets.propertyKpi.capitalizationRate');
        break;
      case "marketValuePerSqFt":
        label = this.translate.instant('widgets.propertyKpi.marketValuePerSqFt');
        break;
      case "reversionaryYield":
        label = this.translate.instant('widgets.propertyKpi.reversionaryYield');
        break;
      case "vacency":
        label = this.translate.instant('widgets.propertyKpiRent.vacency');
        break;
      case "contractualRent":
        label = this.translate.instant('widgets.propertyKpiRent.contractualRent');
        break;
      case "capitalExpenditure":
        label = this.translate.instant('widgets.propertyKpiRent.capitalExpenditure');
        break;
      case "netRent":
        label = this.translate.instant('widgets.propertyKpiRent.netRent');
        break;
      default:
        label = '';
        break;
    }
    return label;
  }

  /**
   * Returns KPI value
  * @param key {string} KPI key
   */
  getKpiValue(key: string) {
    let value = '';
    switch (key) {
      case "overUnderrent":
        {
          if (this.widgetData[key] != null) {
            value = `${this.decimalPipe.transform(Math.abs(this.widgetData[key] * 100), '1.0-2')}%`;
          }
          break;
        }
      case "vacancyRate":
      case "niy":
      case "ney":
      case "nry":
      case "vacency":
      case "reversionaryYield":
      case "capitalizationRate":
      case "netInitialYield": {
        if (this.widgetData[key] != null) {
          value = `${this.decimalPipe.transform(this.widgetData[key] * 100, '1.0-2')}%`;
        }
        break;
      }
      case "lettableArea": {
        if (this.widgetData[key] != null) {
          value = `${this.decimalPipe.transform(this.widgetData[key], '1.0-0')} ${this.unitOfLength}²`;
        }
        break;
      }
      case "totalRentalValue":
      case "contractualRent":
      case "marketValue":
      case "capitalExpenditure":
      case "netRent":
      case "totalGrossRent":
      case "totalContractedRent":
      case "totaNetRent":
      case "totalRentValue":
      case "marketValueSqFt":
      case "marketValuePerSqFt":
        {
          if (this.widgetData[key] != null) {
            value = `${this.currency}${this.decimalPipe.transform(this.widgetData[key], '1.0-0')}`;
          }
          break;
        }
      case "walt":
        {
          if (this.widgetData[key] != null) {
            value = `${this.decimalPipe.transform(this.widgetData[key], '1.0-2')} years`;
          }
          break;
        }

      default: {
        break;
      }
    }

    return value;
  }

}
